import {
  Box,
  Text,
  Grid,
  GridItem,
  IconButton,
  VisuallyHidden,
  Button,
} from '@everlywell/ui-kit';
import { Minus, Plus, TrashSimple } from '@phosphor-icons/react';
import useCart from 'components/Cart/Cart.hooks';
import {
  isQuest360,
  isQuestGalleri,
} from 'components/CheckoutQuest/CheckoutQuest.utils';
import Price from 'components/Price';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { useState } from 'react';
import analytics from 'utils/analytics';
import { pascalCase } from 'utils/cartHelpers';
import { ANALYTICS } from 'utils/constants/analytics';
import { useCartImage } from 'utils/hooks';
import { LineItem } from 'utils/types';

import FallbackProductImage from './assets/Fallback-At-Home-Lab-Test.png';

export type CartLineItemProps = {
  lineItem: LineItem;
  onDecrement: () => void;
  onIncrement: () => void;
  onRemove?: () => void;
  mode?: 'checkout' | 'cart';
  isQuestInPersonTest?: boolean;
  strikeThrough?: boolean;
};

/**
 * Line item card for the cart experience.
 */
function CartLineItem(props: CartLineItemProps): JSX.Element {
  const {
    lineItem,
    onDecrement,
    onIncrement,
    onRemove,
    mode = 'cart',
    isQuestInPersonTest,
    strikeThrough,
  } = props;

  const { product_name: productName, amount, total, quantity } = lineItem;
  const { order } = useCart();

  const productImage = useCartImage(lineItem);

  const [state, setState] = useState<'normal' | 'remove'>('normal');
  const title = state === 'normal' ? productName : `Remove ${productName}?`;

  const isCheckout = mode === 'checkout';

  const productDescription = lineItem.is_subscription
    ? // TODO: this should be dynamic based on the subscription frequency
      `${pascalCase(lineItem.subscription_frequency ?? 'monthly')} subscription`
    : 'One-time purchase';
  const description =
    state === 'normal'
      ? productDescription
      : `Item will be removed from your cart.`;

  const handleDecrement = () => {
    if (quantity === 1) {
      setState('remove');
    } else {
      onDecrement();
    }
  };
  const handleIncrement = () => {
    onIncrement();
  };

  const handleCancelRemove = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: 'Cancel button',
        product: productName,
      },
    });
    setState('normal');
  };

  const productOriginalPriceNumber = parseFloat(amount ?? '0');
  const productPriceNumber = parseFloat(total ?? '0');
  const showMarkdown = productOriginalPriceNumber > productPriceNumber;

  const savingsText = showMarkdown
    ? `Saving ${(
        ((productOriginalPriceNumber - productPriceNumber) /
          productOriginalPriceNumber) *
        100
      ).toFixed(0)}%`
    : '';
  return (
    <Box
      padding={'4'}
      background={isCheckout ? 'tints.white' : 'tints.lightCream'}
      borderRadius={isCheckout ? '12px' : 'lg'}
      borderColor="tints.darkCream"
      borderWidth={isCheckout ? '1px' : '2px'}
      borderStyle="solid"
    >
      <Grid spacing={4}>
        <GridItem width={1 / 5} margin="auto">
          {productImage ? (
            <Box as={GatsbyImage} alt="" image={productImage} />
          ) : (
            <Box as="img" alt="" src={FallbackProductImage} />
          )}
        </GridItem>

        <GridItem width={4 / 5}>
          <Box
            display="flex"
            justifyContent="space-between"
            flexDirection="column"
            height="100%"
            gap={4}
          >
            <Box>
              <Text size={isCheckout ? 'lg' : 'xl'}>{title}</Text>
              <Text
                size={isCheckout ? 'md' : 'lg'}
                marginTop={isCheckout ? 1 : 0}
                color="tints.darkGrey"
              >
                {description}
              </Text>
            </Box>

            <Box>
              {state === 'remove' ? (
                <Grid spacing={2} columns={2}>
                  <GridItem>
                    <Button
                      variant="secondary"
                      colorScheme="redCS"
                      onClick={onRemove}
                      width="100%"
                      size="sm"
                    >
                      Remove
                    </Button>
                  </GridItem>
                  <GridItem>
                    <Button
                      variant="primary"
                      onClick={handleCancelRemove}
                      width="100%"
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </GridItem>
                </Grid>
              ) : (
                <Grid
                  spacing={4}
                  justifyContent="space-between"
                  alignItems={['center']}
                  flexDirection={['row']}
                >
                  <GridItem width="auto">
                    <Box display="flex" gap={2} alignItems="baseline">
                      {/* TODO: remove this once we have a better way to handle Galleri variant pricing */}
                      {strikeThrough &&
                      order &&
                      isQuestGalleri(order) &&
                      isQuest360(order) ? (
                        <>
                          <Price
                            fontSize="lg"
                            price={productPriceNumber + 50}
                            strikeThrough={strikeThrough}
                          />

                          <Price
                            price={productOriginalPriceNumber}
                            fontSize="lg"
                            color="utility.sale"
                          />
                        </>
                      ) : (
                        <>
                          <Price fontSize="lg" price={productPriceNumber} />
                          {showMarkdown && (
                            <Price
                              price={productOriginalPriceNumber}
                              strikeThrough
                              fontSize="md"
                            />
                          )}
                          {savingsText && (
                            <Text size="md" color="utility.sale" fontSize="md">
                              {savingsText}
                            </Text>
                          )}
                        </>
                      )}
                    </Box>
                  </GridItem>
                  <GridItem
                    width="auto"
                    display="flex"
                    gap={2}
                    alignItems="center"
                    sx={{
                      flexGrow: 0,
                    }}
                  >
                    <VisuallyHidden
                      aria-live="polite"
                      role="status"
                    >{`Quantity ${quantity}`}</VisuallyHidden>
                    {!isQuestInPersonTest ? (
                      <>
                        <IconButton
                          icon={<Minus />}
                          variant="primary"
                          colorScheme="whiteCS"
                          size="sm"
                          aria-label="Step down"
                          onClick={handleDecrement}
                          color={'viridian.base'}
                          {...(isCheckout && {
                            backgroundColor: 'tints.cream',
                            color: 'tints.uiGrey',
                          })}
                        />
                        <Text
                          as="span"
                          size={isCheckout ? 'md' : 'lg'}
                          fontWeight="bold"
                          minWidth="30px"
                          textAlign="center"
                          aria-hidden
                        >
                          {quantity}
                        </Text>
                        <IconButton
                          icon={<Plus />}
                          variant="primary"
                          colorScheme="whiteCS"
                          size="sm"
                          aria-label="Step up"
                          onClick={handleIncrement}
                          color={'viridian.base'}
                          {...(isCheckout && {
                            backgroundColor: 'tints.cream',
                            color: 'tints.uiGrey',
                          })}
                        />
                      </>
                    ) : (
                      <IconButton
                        icon={<TrashSimple />}
                        variant="primary"
                        colorScheme="whiteCS"
                        size="sm"
                        aria-label="Step up"
                        onClick={onRemove}
                        color={'viridian.base'}
                        {...(isCheckout && {
                          backgroundColor: 'tints.cream',
                          color: 'tints.uiGrey',
                        })}
                      />
                    )}
                  </GridItem>
                </Grid>
              )}
            </Box>
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
}

export default CartLineItem;
